import { useState } from 'react';
import { PrimaryButton } from '@/components/glitch-ui/primary-button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/useToast';
import { useUpdateOrgScheduleMutation } from '@/lib/api-extensions/scheduler-queries';
import { TIMEZONES } from './scheduler-constants';

interface OrgClockManagerProps {
  organizationId: number;
  organizationName: string;
}

/** Set an organization's timezone + base schedule. Jobs set to INHERIT_ORG follow this clock. */
export function OrgClockManager({ organizationId, organizationName }: OrgClockManagerProps) {
  const updateOrg = useUpdateOrgScheduleMutation();
  const [timezone, setTimezone] = useState('UTC');
  const [baseSchedule, setBaseSchedule] = useState('');

  const save = () => {
    updateOrg.mutate(
      { orgId: organizationId, update: { timezone, base_schedule: baseSchedule } },
      {
        onSuccess: () =>
          toast({
            title: 'Organization clock updated',
            description: `Inheriting jobs for ${organizationName} have been rescheduled.`,
          }),
        onError: (e) =>
          toast({
            title: 'Update failed',
            description: e instanceof Error ? e.message : 'Could not update the organization clock.',
            variant: 'destructive',
          }),
      },
    );
  };

  return (
    <div className="rounded-lg border bg-white p-6 shadow-sm">
      <h2 className="text-base font-semibold text-gray-900">Organization Clock</h2>
      <p className="mt-1 text-sm text-gray-500">
        Sets the default timezone and time for {organizationName}. Jobs set to INHERIT_ORG follow this clock; a job with
        its own schedule ignores it.
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-gray-500">Timezone</Label>
          <Select value={timezone} onValueChange={setTimezone}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {TIMEZONES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-gray-500">Base Schedule</Label>
          <Input
            value={baseSchedule}
            onChange={(e) => setBaseSchedule(e.target.value)}
            placeholder="0 9 * * *"
            className="font-mono"
          />
        </div>
      </div>
      <div className="mt-4">
        <PrimaryButton
          label={updateOrg.isPending ? 'Saving...' : 'Save Clock'}
          onClick={save}
          disabled={updateOrg.isPending}
        />
      </div>
    </div>
  );
}
